import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import logoLight from '@/assets/sellaris-lockup-light.svg';
import logoDark from '@/assets/sellaris-lockup-dark.svg';
import logoMark from '@/assets/sellaris-icon.svg';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  linkTo?: string;
  onClick?: () => void;
  className?: string;
  showText?: boolean;
}

export const Logo = forwardRef<HTMLSpanElement, LogoProps>(
  ({ size = 'md', linkTo, onClick, className, showText = true }, ref) => {
    // Full lockup (icon + wordmark) is ~3.3:1, so it needs a touch more height
    // than the icon-only mark for the wordmark to read at the same optical size.
    const lockupHeights = { sm: 'h-5', md: 'h-6', lg: 'h-8' } as const;
    const markHeights = { sm: 'h-5', md: 'h-6', lg: 'h-7' } as const;

    const imgClass = cn(
      showText ? lockupHeights[size] : markHeights[size],
      'block w-auto object-contain shrink-0 self-center',
    );

    const logoContent = (
      <span ref={ref} className={cn('inline-flex items-center leading-none', className)}>
        {showText ? (
          <>
            <img src={logoLight} alt="Sellaris" className={cn(imgClass, 'dark:hidden')} />
            <img src={logoDark} alt="Sellaris" className={cn(imgClass, 'hidden dark:block')} />
            <span
              aria-hidden="true"
              className={cn(
                'self-start font-semibold text-foreground leading-none tracking-[0.02em] select-none',
                size === 'lg'
                  ? 'text-[8px] ml-[3px] mt-[3px]'
                  : size === 'md'
                    ? 'text-[7px] ml-[2px] mt-[2px]'
                    : 'text-[6px] ml-[2px] mt-[2px]',
              )}
            >
              TM
            </span>
          </>
        ) : (
          <img src={logoMark} alt="Sellaris" className={imgClass} />
        )}
      </span>
    );



    if (linkTo) {
      return (
        <Link to={linkTo} className="inline-flex items-center leading-none hover:opacity-80 transition-opacity">
          {logoContent}
        </Link>
      );
    }

    if (onClick) {
      return (
        <button onClick={onClick} className="inline-flex items-center leading-none hover:opacity-80 transition-opacity">
          {logoContent}
        </button>
      );
    }


    return logoContent;
  }
);

Logo.displayName = 'Logo';
